/**
 * Analytics wrapper — PostHog (via <script> snippet) with console-log fallback.
 *
 * Why no npm dep:
 *  · We want zero bundle impact until analytics is actually wired.
 *  · PostHog ships a drop-in snippet for `public/index.html` that exposes
 *    `window.posthog`. That's the path of least resistance.
 *  · If we ever need fine-grained control (feature flags in bundle, SSR),
 *    swap this file for a real import — the public API below stays the same.
 *
 * To enable real tracking in production:
 *   1. Copy the PostHog "snippet" into `frontend/public/index.html`
 *      (dashboard → Project Settings → Snippet). Replace PROJECT_KEY.
 *   2. Rebuild. The wrapper auto-detects window.posthog and switches from
 *      console stub to real calls. No code change required here.
 *
 * Event names live in EVT — keep in sync with
 * docs/implementation-plan-2026-04-22.md §4.5.
 */

const IS_DEV = process.env.NODE_ENV !== 'production'

function getPosthog() {
  // `window.posthog` is the API exposed by the drop-in snippet.
  // Access it lazily so the bundle never statically references it.
  if (typeof window === 'undefined') return null
  return window.posthog || null
}

function stubLog(kind, ...args) {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[analytics:${kind}]`, ...args)
  }
}

// ── Public API ─────────────────────────────────────────────────────────────

export function initAnalytics() {
  const ph = getPosthog()
  if (ph) {
    if (IS_DEV) stubLog('init', 'PostHog detected via window.posthog')
  } else {
    if (IS_DEV) stubLog('init', 'no window.posthog — stub mode')
  }
}

export function track(event, properties = {}) {
  const ph = getPosthog()
  if (ph) {
    ph.capture(event, properties)
  } else {
    stubLog('track', event, properties)
  }
}

export function identify(userId, traits = {}) {
  const ph = getPosthog()
  if (ph) {
    ph.identify(userId, traits)
  } else {
    stubLog('identify', userId, traits)
  }
}

export function reset() {
  const ph = getPosthog()
  if (ph) {
    ph.reset()
  } else {
    stubLog('reset')
  }
}

export function pageView(path) {
  const ph = getPosthog()
  if (ph) {
    ph.capture('$pageview', { $current_url: window.location.href, path })
  } else {
    stubLog('pageview', path)
  }
}

// ── Event name constants (matches implementation-plan-2026-04-22.md §4.5) ──

export const EVT = Object.freeze({
  // Auth
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',

  // Onboarding + F1.b gift
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_STEP7_SHOWN: 'onboarding_step7_shown',
  ONBOARDING_IDEA_SELECTED: 'onboarding_idea_selected',
  ONBOARDING_GIFT_GENERATION_STARTED: 'onboarding_gift_generation_started',
  ONBOARDING_GIFT_GENERATION_COMPLETED: 'onboarding_gift_generation_completed',
  ONBOARDING_GIFT_GENERATION_FAILED: 'onboarding_gift_generation_failed',
  FIRST_GIFT_SCRIPT_FIRST_EDIT: 'first_gift_script_first_edit',

  // Landing F1.a
  LANDING_EXAMPLES_SCROLLED_INTO_VIEW: 'landing_examples_scrolled_into_view',
  LANDING_EXAMPLE_EXPANDED: 'landing_example_expanded',
  LANDING_EXAMPLE_URL_COPIED: 'landing_example_url_copied',

  // Scripts
  FIRST_SCRIPT_GENERATED: 'first_script_generated',
  SCRIPT_GENERATION_STARTED: 'script_generation_started',
  SCRIPT_GENERATION_COMPLETED: 'script_generation_completed',
  SCRIPT_GENERATION_FAILED: 'script_generation_failed',

  // Credits & billing
  CREDIT_SPENT: 'credit_spent',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',

  // F4 refine (future)
  REFINE_MENU_OPENED: 'refine_menu_opened',
  REFINE_APPLIED: 'refine_applied',
})
